import { RemoveRedEye as RemoveRedEyeIcon } from '@mui/icons-material';
import { FormControlLabel, Switch } from '@mui/material';
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form';
import { SimpleDialog } from 'src/components/modal';
import { SelectDropdownWidget } from 'src/components/widget';
import { getGridOptions } from 'src/hooks/api/layouts/helpers';
import {
  IDisclaimerPosition,
  IDisclaimerWidth,
  ILayout,
  IScreenOrientation,
} from 'src/interfaces';
import { lang } from 'src/utils';

import { useScreenConfigModal } from './hooks/ScreenConfigModal.hook';

export interface IScreenConfigModal {
  open: boolean;
  layout: ILayout | undefined;
  screenIndex: number;
  formMethods: UseFormReturn<FieldValues, any, undefined>;
  onSaveScreenConfigModal: (value: string) => void;
  onCloseModal: () => void;
}

export const orientationSelectOptions = [
  { label: IScreenOrientation.HORIZONTAL, value: 'horizontal' },
  { label: IScreenOrientation.VERTICAL, value: 'vertical' },
];

export const placementSelectOptions = [
  {
    label: IDisclaimerPosition.TOP,
    value: IDisclaimerPosition.TOP,
  },
  {
    label: IDisclaimerPosition.BOTTOM,
    value: IDisclaimerPosition.BOTTOM,
  },
];

export const sizeSelectOptions = [
  {
    label: IDisclaimerWidth.HALF,
    value: IDisclaimerWidth.HALF,
  },
  {
    label: IDisclaimerWidth.FULL,
    value: IDisclaimerWidth.FULL,
  },
];

export const ScreenConfigModal = (props: IScreenConfigModal) => {
  const { open, layout, screenIndex, formMethods } = props;
  const {
    shouldDisableOptions,
    isDisclaimerChecked,
    validateField,
    handleScreenConfigClose,
    handleSaveScreenConfigModal,
  } = useScreenConfigModal(props);

  return (
    <SimpleDialog
      closeOnBackdropClick={false}
      open={open}
      title={lang.editLayoutsSidebar.configureScreen(
        `screen ${screenIndex + 1}`,
      )}
      confirmText={lang.confirm}
      onClose={handleScreenConfigClose}
      onClickCancel={handleScreenConfigClose}
      onClickConfirm={formMethods.handleSubmit(handleSaveScreenConfigModal)}>
      <Controller
        name="orientation"
        control={formMethods.control}
        defaultValue=""
        render={({ field }) => (
          <SelectDropdownWidget
            {...field}
            selectOptions={orientationSelectOptions}
            label={lang.scheduleComponent.screenOrientation}
            value={field?.value}
            IconComponent={RemoveRedEyeIcon}
            disabled
          />
        )}
      />

      <Controller
        name="screen_grid"
        control={formMethods.control}
        rules={{ required: lang.formErrorMsg.required(lang.grid) }}
        defaultValue=""
        render={({ field, fieldState }) => (
          <SelectDropdownWidget
            {...field}
            label={lang.grid}
            value={field?.value}
            onChange={(e) => field?.onChange(e)}
            selectOptions={getGridOptions(layout!.screens?.[screenIndex])}
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
            disabled={shouldDisableOptions()}
            {...(shouldDisableOptions()
              ? {
                  IconComponent: RemoveRedEyeIcon,
                }
              : {})}
          />
        )}
      />
      <Controller
        name="disclaimer"
        control={formMethods.control}
        defaultValue={false}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                {...field}
                data-testid="disclaimer-switch"
                checked={field?.value}
                onChange={(e) => {
                  field?.onChange(e.target.checked);
                  formMethods.setValue('placement', '');
                  formMethods.setValue('size', '');
                }}
                disabled={shouldDisableOptions()}
              />
            }
            label={lang.assemblyLayouts.screenSetup.disclaimerLabel}
          />
        )}
      />

      <Controller
        name="placement"
        control={formMethods.control}
        rules={{ validate: () => validateField('placement') }}
        defaultValue=""
        render={({ field, fieldState }) => (
          <SelectDropdownWidget
            {...field}
            label={lang.assemblyLayouts.screenSetup.placementLabel}
            disabled={!isDisclaimerChecked}
            value={field?.value}
            data-testid="Placement"
            onChange={(e) => field?.onChange(e)}
            selectOptions={placementSelectOptions}
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
          />
        )}
      />

      <Controller
        name="size"
        control={formMethods.control}
        rules={{ validate: () => validateField('size') }}
        defaultValue=""
        render={({ field, fieldState }) => (
          <SelectDropdownWidget
            {...field}
            label={lang.assemblyLayouts.screenSetup.sizeLabel}
            disabled={!isDisclaimerChecked}
            value={field?.value}
            onChange={(e) => field?.onChange(e)}
            selectOptions={sizeSelectOptions}
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
          />
        )}
      />
    </SimpleDialog>
  );
};
